import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { apiFetch, apiPut } from "@/lib/api";
import { fileKeys, folderKeys } from "@/lib/query";

export interface ProjectFile {
  id: string;
  orgId: string;
  projectId: string | null;
  parentType: string;
  parentId: string;
  fileName: string;
  storageKey: string;
  mimeType: string | null;
  fileSizeBytes: number | null;
  description: string | null;
  version: number;
  isLatestVersion: boolean;
  folderId: string | null;
  folderAssignedBy: string | null;
  documentType: string | null;
  isConfidential: boolean;
  createdAt: string;
}

export interface FileUploadEntry {
  id: string;
  file: File;
  status: "pending" | "uploading" | "success" | "error" | "duplicate";
  progress: number;
  errorMessage?: string;
  existingFileId?: string;
}

interface UploadOptions {
  documentType?: string | null;
  description?: string | null;
  isConfidential?: boolean;
}

export function useFolderFiles(projectId: string, folderId: string) {
  return useQuery({
    queryKey: fileKeys.folder(projectId, folderId),
    queryFn: () => apiFetch<ProjectFile[]>(`/projects/${projectId}/folders/${folderId}/files`),
    enabled: !!projectId && !!folderId,
  });
}

export function useUnfiledFiles(projectId: string) {
  return useQuery({
    queryKey: fileKeys.unfiled(projectId),
    queryFn: () => apiFetch<ProjectFile[]>(`/projects/${projectId}/unfiled-files`),
    enabled: !!projectId,
  });
}

export function useMoveFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, folderId }: { fileId: string; folderId: string | null }) =>
      apiPut(`/files/${fileId}/move-folder`, { folderId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fileKeys.all });
      toast.success("File moved successfully");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useFolderUpload(projectId: string, folderId: string) {
  const [uploads, setUploads] = useState<FileUploadEntry[]>([]);
  const qc = useQueryClient();
  const activeCountRef = useRef(0);
  const queueRef = useRef<Array<{ entry: FileUploadEntry; opts: UploadOptions }>>([]);
  // Ref so doUpload can call the latest processQueue without a circular useCallback dep
  const processQueueRef = useRef<() => void>(() => {});

  const updateEntry = useCallback((id: string, patch: Partial<FileUploadEntry>) => {
    setUploads((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, []);

  const doUpload = useCallback(
    (entry: FileUploadEntry, opts: UploadOptions, extraFields?: Record<string, string>) => {
      updateEntry(entry.id, { status: "uploading", progress: 0 });
      activeCountRef.current++;

      const formData = new FormData();
      formData.append("file", entry.file);
      if (opts.documentType) formData.append("documentType", opts.documentType);
      if (opts.description) formData.append("description", opts.description);
      if (opts.isConfidential) formData.append("isConfidential", "true");
      if (extraFields) {
        for (const [k, v] of Object.entries(extraFields)) {
          formData.append(k, v);
        }
      }

      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          updateEntry(entry.id, { progress: Math.round((e.loaded / e.total) * 100) });
        }
      });
      xhr.addEventListener("load", () => {
        activeCountRef.current--;
        if (xhr.status === 201) {
          updateEntry(entry.id, { status: "success", progress: 100 });
          // Refresh both the folder's file list (right panel) and the project
          // folders query (left tree), so the rollup badges update.
          qc.invalidateQueries({ queryKey: fileKeys.folder(projectId, folderId) });
          qc.invalidateQueries({ queryKey: folderKeys.project(projectId) });
        } else if (xhr.status === 409) {
          try {
            const body = JSON.parse(xhr.responseText) as {
              error?: string;
              existingFileId?: string;
            };
            if (body.error === "duplicate_detected") {
              updateEntry(entry.id, { status: "duplicate", existingFileId: body.existingFileId });
            } else {
              updateEntry(entry.id, { status: "error", errorMessage: "Upload conflict" });
            }
          } catch {
            updateEntry(entry.id, { status: "error", errorMessage: "Upload conflict" });
          }
        } else {
          try {
            const body = JSON.parse(xhr.responseText) as { error?: string };
            updateEntry(entry.id, { status: "error", errorMessage: body.error ?? "Upload failed" });
          } catch {
            updateEntry(entry.id, { status: "error", errorMessage: "Upload failed" });
          }
        }
        processQueueRef.current();
      });
      xhr.addEventListener("error", () => {
        activeCountRef.current--;
        updateEntry(entry.id, { status: "error", errorMessage: "Network error" });
        processQueueRef.current();
      });
      xhr.open("POST", `/api/projects/${projectId}/folders/${folderId}/upload`);
      xhr.send(formData);
    },
    [projectId, folderId, updateEntry, qc],
  );

  const processQueue = useCallback(() => {
    while (activeCountRef.current < 3 && queueRef.current.length > 0) {
      const item = queueRef.current.shift();
      if (item) doUpload(item.entry, item.opts);
    }
  }, [doUpload]);

  // Keep ref in sync so XHR callbacks always call the latest processQueue
  processQueueRef.current = processQueue;

  const startUpload = useCallback(
    (newFiles: File[], opts: UploadOptions = {}) => {
      const entries: FileUploadEntry[] = newFiles.map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        status: "pending" as const,
        progress: 0,
      }));
      setUploads((prev) => [...prev, ...entries]);
      for (const e of entries) {
        queueRef.current.push({ entry: e, opts });
      }
      processQueue();
    },
    [processQueue],
  );

  const retryUpload = useCallback(
    (id: string) => {
      setUploads((prev) => {
        const entry = prev.find((e) => e.id === id);
        if (!entry) return prev;
        const reset = {
          ...entry,
          status: "pending" as const,
          progress: 0,
          errorMessage: undefined,
        };
        queueRef.current.push({ entry: reset, opts: {} });
        // Use setTimeout so the queue processes after state settles
        setTimeout(() => processQueue(), 0);
        return prev.map((e) => (e.id === id ? reset : e));
      });
    },
    [processQueue],
  );

  const resolveVersion = useCallback(
    (id: string, choice: "version" | "rename", newName?: string) => {
      setUploads((prev) => {
        const entry = prev.find((e) => e.id === id);
        if (!entry) return prev;
        const reset = {
          ...entry,
          status: "uploading" as const,
          progress: 0,
          existingFileId: undefined,
          errorMessage: undefined,
        };
        const extra: Record<string, string> =
          choice === "version"
            ? { forceVersion: "true" }
            : { newName: newName ?? `${entry.file.name.replace(/(\.[^.]+)$/, "")}_copy$1` };
        doUpload(reset, {}, extra);
        return prev.map((e) => (e.id === id ? reset : e));
      });
    },
    [doUpload],
  );

  const clearCompleted = useCallback(() => {
    setUploads((prev) => prev.filter((e) => e.status !== "success"));
  }, []);

  return { uploads, startUpload, retryUpload, resolveVersion, clearCompleted };
}
